/*
 * Client-scoped invalidation bus.
 *
 * The client manager agent lives in the global agent panel, outside the
 * client profile's React tree, so its `data-invalidate` parts cannot reach the
 * profile through context. This module-level emitter bridges them: the panel
 * calls `invalidateClient(clientId, targets)` when a tool reports stale
 * caches, and the profile's context and tabs subscribe with
 * `useOnClientInvalidate` to refetch exactly what changed. Mirrors
 * `useOnInvalidate` in the Case IDE, keyed by client so a profile only reacts
 * to its own client's agent.
 */
import { useEffect, useRef } from "react";

import type { ClientInvalidateTarget } from "../../caseIde/types";

export type ClientInvalidateListener = (
  targets: ClientInvalidateTarget[]
) => void;

const listeners = new Map<string, Set<ClientInvalidateListener>>();

const CLIENT_TARGETS: ReadonlySet<string> = new Set<ClientInvalidateTarget>([
  "client",
  "cases",
  "documents",
  "intakes",
  "reminders",
  "records",
  "activity"
]);

/** Keep only the client-scoped targets out of a mixed `data-invalidate` list. */
export function clientTargets(
  targets: readonly string[]
): ClientInvalidateTarget[] {
  return targets.filter((t): t is ClientInvalidateTarget =>
    CLIENT_TARGETS.has(t)
  );
}

/** Notify everything mounted for `clientId` that `targets` went stale. */
export function invalidateClient(
  clientId: string,
  targets: readonly string[]
): void {
  const id = String(clientId ?? "").trim();
  const hit = clientTargets(targets);
  if (!id || hit.length === 0) return;
  const set = listeners.get(id);
  if (!set) return;
  // Copy: a listener may unsubscribe while we iterate.
  for (const fn of Array.from(set)) fn(hit);
}

/** Subscribe to invalidations for one client; returns the unsubscribe. */
export function subscribeClientInvalidation(
  clientId: string,
  listener: ClientInvalidateListener
): () => void {
  const id = String(clientId ?? "").trim();
  if (!id) return () => undefined;
  let set = listeners.get(id);
  if (!set) {
    set = new Set();
    listeners.set(id, set);
  }
  set.add(listener);
  return () => {
    const current = listeners.get(id);
    if (!current) return;
    current.delete(listener);
    if (current.size === 0) listeners.delete(id);
  };
}

/**
 * Run `refetch` when any of `targets` is invalidated for `clientId`. The
 * latest `refetch` is always used, so callers need not memoize it.
 */
export function useOnClientInvalidate(
  clientId: string | null | undefined,
  targets: readonly ClientInvalidateTarget[],
  refetch: (matched: ClientInvalidateTarget[]) => void
): void {
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const key = targets.join("|");
  useEffect(() => {
    if (!clientId) return;
    const wanted = new Set(key.split("|").filter(Boolean));
    return subscribeClientInvalidation(clientId, (hit) => {
      const matched = hit.filter((t) => wanted.has(t));
      if (matched.length) refetchRef.current(matched);
    });
  }, [clientId, key]);
}

/** Test hook: number of live subscribers for a client. */
export function _listenerCount(clientId: string): number {
  return listeners.get(clientId)?.size ?? 0;
}
